import { useEffect, useState } from 'react';
import { initializeRegionData, applyTickGrowth, calculateTotalProtectedData, calculateImmutableCopies } from '../../../mocks/activityData';
import RiskJobsModal from './RiskJobsModal';

interface Metric {
  label: string;
  value: string;
  unit: string;
  change: number;
}

// Baseline values (3 days ago)
const BASELINE_COVERAGE = 98.4;

// Air-Gapped Vault regions with status
const airGappedRegions = [
  { name: 'China Mainland', status: 'Operational', recoverability: 97.2 },
  { name: 'Singapore', status: 'Operational', recoverability: 98.1 },
  { name: 'Europe', status: 'Operational', recoverability: 96.8 },
  { name: 'United States', status: 'Operational', recoverability: 97.5 },
];

// Time-of-day modulation helper
const getTimeOfDayModifier = (): number => {
  const now = new Date();
  const hour = now.getHours();
  const day = now.getDay();
  
  // Weekend / holiday check
  const isWeekend = day === 0 || day === 6;
  if (isWeekend) {
    return 0.75 + Math.random() * 0.1; // -25% to -15%
  }
  
  // Business hours (9 AM - 6 PM)
  if (hour >= 9 && hour < 18) {
    return 1.05 + Math.random() * 0.1; // +5% to +15%
  }
  
  // Off hours
  return 0.90 + Math.random() * 0.05; // -10% to -5%
};

// Calculate active backup operations based on total protected data
const calculateActiveOperations = (totalProtectedDataTB: number): number => {
  // Base concurrency: 0.20-0.25 operations per TB
  const baseRate = 0.20 + Math.random() * 0.05;
  const baseOperations = totalProtectedDataTB * baseRate;
  
  // Apply time-of-day modulation
  const timeModifier = getTimeOfDayModifier();
  
  // Apply short-term jitter (±1% to ±3%)
  const jitter = 1 + (Math.random() - 0.5) * 0.06; // ±3%
  
  const finalOperations = baseOperations * timeModifier * jitter;
  
  return Math.floor(finalOperations);
};

export default function LeftMetrics() {
  // Initialize with calculated values
  const initialRegionData = initializeRegionData();
  const initialTotal = calculateTotalProtectedData(initialRegionData);
  const initialCopies = calculateImmutableCopies(initialTotal);
  const initialOperations = calculateActiveOperations(initialTotal);
  
  // State for region data (single source of truth)
  const [regionData, setRegionData] = useState<Record<string, number>>(initialRegionData);
  const [totalProtectedData, setTotalProtectedData] = useState<number>(initialTotal);
  const [immutableCopies, setImmutableCopies] = useState<number>(initialCopies);
  const [activeOperations, setActiveOperations] = useState<number>(initialOperations);
  
  const [metrics, setMetrics] = useState<Metric[]>([
    { label: 'Total Protected Data', value: initialTotal.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }), unit: 'TB', change: 0 },
    { label: 'Active Backup Operations', value: initialOperations.toLocaleString(), unit: '', change: 0 },
    { label: 'Immutable Copies', value: Math.floor(initialCopies).toLocaleString(), unit: '', change: 0 },
    { label: 'Air-Gapped Vaults', value: 'Enabled in 4 Regions', unit: '', change: 0 },
    { label: 'Global Coverage', value: '98.6', unit: '%', change: 0 }
  ]);

  // CTO-level resilience indicators
  const [recoverabilityScore, setRecoverabilityScore] = useState(96.8);
  
  // SLA Coverage Distribution (RPO-based)
  const [rpoUnder15Min, setRpoUnder15Min] = useState(68.2); // %
  const [rpoUnder1Hour, setRpoUnder1Hour] = useState(26.4); // %
  const [rpoOver1Hour, setRpoOver1Hour] = useState(5.4); // %
  
  // At-Risk Jobs (0.5% - 1.5% of active operations)
  const [atRiskJobs, setAtRiskJobs] = useState(24);
  const [atRiskPercentage, setAtRiskPercentage] = useState(0.5);
  
  // Modal states
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [showSLAModal, setShowSLAModal] = useState(false);

  const [showVaultDetails, setShowVaultDetails] = useState(false);
  const [showChange, setShowChange] = useState(false);

  // AI Operations Summary (Last 24h)
  const [aiIssuesDetected, setAiIssuesDetected] = useState(36);
  const [aiAutoResolved, setAiAutoResolved] = useState(28);
  const [aiEscalated, setAiEscalated] = useState(8);
  const [aiPreventedBreaches, setAiPreventedBreaches] = useState(19);

  // Track previous values for change calculation
  const [prevTotal, setPrevTotal] = useState(initialTotal);
  const [prevCopies, setPrevCopies] = useState(initialCopies);
  const [prevOperations, setPrevOperations] = useState(initialOperations);

  // Slow updates for CTO-level indicators (every 2 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      // Recoverability Score - very slow drift (96.5% - 97.5%)
      setRecoverabilityScore(prev => {
        const change = (Math.random() - 0.5) * 0.1;
        return Math.max(96.5, Math.min(97.5, prev + change));
      });

      // SLA Coverage Distribution - very slow changes
      setRpoUnder15Min(prev => {
        const change = (Math.random() - 0.5) * 0.3;
        return Math.max(66, Math.min(70, prev + change));
      });
      
      setRpoUnder1Hour(prev => {
        const change = (Math.random() - 0.5) * 0.2;
        return Math.max(25, Math.min(28, prev + change));
      });
      
      setRpoOver1Hour(prev => {
        const change = (Math.random() - 0.5) * 0.2;
        return Math.max(4, Math.min(7, prev + change));
      });

      // At-Risk Jobs - proportional to active operations (0.5% - 1.5%)
      const currentOps = activeOperations;
      const riskPercentage = 0.005 + Math.random() * 0.01; // 0.5% - 1.5%
      const riskJobs = Math.floor(currentOps * riskPercentage);
      
      setAtRiskJobs(riskJobs);
      setAtRiskPercentage(riskPercentage * 100);

      // AI Operations Summary - realistic numbers
      // Auto-resolved should be > escalated
      const detected = Math.floor(Math.random() * 10) + 32; // 32-42
      const autoResolved = Math.floor(detected * (0.75 + Math.random() * 0.1)); // 75-85% auto-resolved
      const escalated = detected - autoResolved;
      
      setAiIssuesDetected(detected);
      setAiAutoResolved(autoResolved);
      setAiEscalated(escalated);
      
      // AI prevented breaches - should be higher than current at-risk jobs
      const prevented = Math.floor(riskJobs * (0.7 + Math.random() * 0.3)); // 70-100% of at-risk
      setAiPreventedBreaches(prevented);
    }, 2 * 60 * 1000); // Every 2 minutes

    return () => clearInterval(interval);
  }, [activeOperations]);

  // Tick-based growth updates (every 30 seconds) - SINGLE SOURCE OF TRUTH
  useEffect(() => {
    const interval = setInterval(() => {
      setShowChange(true);
      
      // Apply tick growth to region data
      setRegionData(prevRegionData => {
        const updatedRegionData = applyTickGrowth(prevRegionData);
        
        // Calculate derived values from updated region data
        const newTotal = calculateTotalProtectedData(updatedRegionData);
        const deltaTotalTB = newTotal - prevTotal;
        
        // Update total protected data
        setTotalProtectedData(newTotal);
        setPrevTotal(newTotal);
        
        // Update immutable copies (derived from total growth)
        const immutableDelta = deltaTotalTB * 3.0; // k = 3.0 (stable multiplier)
        setImmutableCopies(prev => {
          const newCopies = prev + immutableDelta;
          setPrevCopies(newCopies);
          return newCopies;
        });
        
        // Update active backup operations (derived from total protected data)
        const newOperations = calculateActiveOperations(newTotal);
        const operationsDelta = newOperations - prevOperations;
        setActiveOperations(newOperations);
        setPrevOperations(newOperations);
        
        // Update metrics display
        setMetrics(prev => prev.map((metric, index) => {
          switch (index) {
            case 0: {
              // Total Protected Data - exact sum of regions with one decimal
              return { 
                ...metric, 
                value: newTotal.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
                change: deltaTotalTB 
              };
            }
            
            case 1: {
              // Active Backup Operations - time-variant concurrency
              return { 
                ...metric, 
                value: newOperations.toLocaleString(), 
                change: operationsDelta 
              };
            }
            
            case 2: {
              // Immutable Copies - derived from Total Protected Data
              const newCopiesValue = immutableCopies + immutableDelta;
              return { 
                ...metric, 
                value: Math.floor(newCopiesValue).toLocaleString(), 
                change: immutableDelta 
              };
            }
            
            case 3: {
              // Air-Gapped Vaults - regional capability display
              return metric; // No change, handled separately
            }
            
            case 4: {
              // Global Coverage - very slight fluctuations
              const currentValue = parseFloat(metric.value);
              const change = (Math.random() - 0.5) * 0.02;
              const newValue = Math.max(98.4, Math.min(98.9, currentValue + change));
              return { ...metric, value: newValue.toFixed(1), change };
            }
            
            default:
              return metric;
          }
        }));
        
        return updatedRegionData;
      });

      // Hide change indicators after 1 second
      setTimeout(() => setShowChange(false), 1000);
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [prevTotal, prevCopies, immutableCopies, prevOperations]);

  return (
    <>
      <div className="absolute left-0 top-20 bottom-0 w-96 pl-8 pr-6 pt-6 pb-20 z-20 overflow-y-auto hide-scrollbar">
        <div className="space-y-6">
          {metrics.map((metric, index) => (
            <div key={index} className="flex flex-col">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5 font-medium">
                {metric.label}
              </div>
              <div className="flex items-baseline gap-2">
                {index !== 3 && (
                  <span className="text-4xl font-bold text-white font-mono tracking-tight transition-all duration-500">
                    {metric.value}
                  </span>
                )}
                {metric.unit && (
                  <span className="text-xl text-slate-400 font-medium">
                    {metric.unit}
                  </span>
                )}
                {metric.change !== 0 && index !== 3 && (
                  <span 
                    className={`text-xs ml-2 transition-opacity duration-300 ${
                      metric.change > 0 ? 'text-green-400' : 'text-red-400'
                    } ${showChange ? 'opacity-100' : 'opacity-0'}`}
                  >
                    {metric.change > 0 ? '↑' : '↓'}
                  </span>
                )}
              </div>
              
              {/* Active Backup Operations - AI stabilization note */}
              {index === 1 && (
                <div className="text-[9px] text-slate-600 mt-1.5 italic leading-relaxed">
                  Concurrency stabilized by AI-driven scheduling and workload throttling
                </div>
              )}
              
              {/* Air-Gapped Vaults regional details */}
              {index === 3 && (
                <div className="mt-2">
                  <button
                    onClick={() => setShowVaultDetails(!showVaultDetails)}
                    className="text-xs text-teal-400 hover:text-teal-300 transition-colors cursor-pointer flex items-center gap-1.5 font-medium"
                  >
                    {showVaultDetails ? '▼' : '▶'} Regional Status
                  </button>
                  
                  {showVaultDetails && (
                    <div className="mt-2.5 space-y-2 pl-3 border-l border-slate-700/30">
                      {airGappedRegions.map((region, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-3 text-xs">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                region.status === 'Operational'
                                  ? 'bg-emerald-400'
                                  : region.status === 'Maintenance'
                                  ? 'bg-amber-400'
                                  : 'bg-red-400'
                              }`}
                            />
                            <span className="text-slate-400 font-normal">{region.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-xs font-medium ${
                                region.status === 'Operational'
                                  ? 'text-emerald-400'
                                  : region.status === 'Maintenance'
                                  ? 'text-amber-400'
                                  : 'text-red-400'
                              }`}
                            >
                              {region.status}
                            </span>
                            <span className="text-[10px] text-slate-500">
                              {region.recoverability.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* CTO-Level Resilience Indicators */}
          <div className="pt-6 space-y-6 border-t border-slate-800/30">
            {/* Recoverability Score */}
            <div className="flex flex-col">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5 font-medium">
                Recoverability Score
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-emerald-400 font-mono tracking-tight">
                  {recoverabilityScore.toFixed(1)}
                </span>
                <span className="text-lg text-slate-400 font-medium">%</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-1.5 leading-relaxed">
                Verified restorable data
              </div>
              <div className="text-[9px] text-slate-600 mt-1 italic leading-relaxed">
                Remaining workloads under verification or policy-exempt
              </div>
            </div>

            {/* SLA Coverage Distribution (RPO-based) */}
            <div className="flex flex-col">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5 font-medium">
                SLA Coverage Distribution
              </div>
              <div className="space-y-2 mt-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">RPO ≤ 15 min</span>
                  <span className="text-cyan-400 font-mono font-medium">{rpoUnder15Min.toFixed(1)}%</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">RPO ≤ 1 hour</span>
                  <span className="text-cyan-400 font-mono font-medium">{rpoUnder1Hour.toFixed(1)}%</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">RPO &gt; 1 hour</span>
                  <span className="text-cyan-400 font-mono font-medium">{rpoOver1Hour.toFixed(1)}%</span>
                </div>
              </div>
              <div className="text-[10px] text-slate-500 mt-2 leading-relaxed">
                SLA-protected workloads only
              </div>
              <div className="text-[9px] text-slate-600 mt-1 italic leading-relaxed">
                Cold and compliance data excluded
              </div>
              <div className="text-[9px] text-slate-500 mt-2 leading-relaxed">
                SLA stability maintained through AI-driven scheduling and retry optimization
              </div>
            </div>

            {/* At-Risk Jobs - With AI Prevention Context */}
            <div className="flex flex-col">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5 font-medium">
                At-Risk Jobs
              </div>
              <button
                onClick={() => setShowRiskModal(true)}
                className="flex items-baseline gap-2 cursor-pointer hover:opacity-80 transition-opacity text-left"
              >
                <span className="text-2xl font-bold text-amber-400 font-mono tracking-tight">
                  {atRiskJobs}
                </span>
                <span className="text-sm text-slate-400 font-medium">
                  ({atRiskPercentage.toFixed(2)}%)
                </span>
              </button>
              <div className="text-[10px] text-slate-500 mt-1.5 leading-relaxed">
                Approaching SLA threshold · Click for details
              </div>
              <div className="text-[9px] text-emerald-400/80 mt-2 leading-relaxed">
                AI prevented SLA breach for {aiPreventedBreaches} jobs in last 24h
              </div>
            </div>

            {/* SLA Threshold Awareness - Clickable */}
            <div className="flex flex-col">
              <button
                onClick={() => setShowSLAModal(true)}
                className="text-xs text-amber-400/80 hover:text-amber-400 transition-colors cursor-pointer text-left font-medium"
              >
                → View SLA threshold status
              </button>
            </div>

            {/* AI Operational Impact - Reframed as System Behavior */}
            <div className="pt-6 border-t border-slate-800/30">
              <div className="flex flex-col">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2.5 font-medium">
                  AI Operational Impact (24h)
                </div>
                <div className="space-y-2">
                  <div className="flex items-start gap-2 text-xs">
                    <span className="text-slate-400 leading-relaxed">{aiIssuesDetected} potential issues detected early</span>
                  </div>
                  <div className="flex items-start gap-2 text-xs">
                    <span className="text-emerald-400/90 leading-relaxed">{aiAutoResolved} resolved autonomously before SLA impact</span>
                  </div>
                  <div className="flex items-start gap-2 text-xs">
                    <span className="text-slate-400 leading-relaxed">{aiEscalated} escalated with mitigation applied</span>
                  </div>
                </div>
                <div className="text-[9px] text-slate-600 mt-2.5 italic leading-relaxed">
                  AI operates within policy boundaries to maintain system stability
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Jobs Modal */}
      {showRiskModal && (
        <RiskJobsModal
          atRiskJobs={atRiskJobs}
          totalJobs={activeOperations}
          onClose={() => setShowRiskModal(false)}
        />
      )}

      {/* SLA Threshold Modal */}
      {showSLAModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-2xl w-[600px] max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">SLA Threshold Status</h3>
              <button
                onClick={() => setShowSLAModal(false)}
                className="text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="px-6 py-5 space-y-4 overflow-y-auto max-h-[calc(80vh-120px)]">
              <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <i className="ri-alert-line text-amber-400 text-xl mt-0.5"></i>
                <div className="flex-1">
                  <div className="text-sm font-medium text-amber-400 mb-1">
                    Early Awareness Alert
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed">
                    {atRiskJobs} backup jobs are approaching their SLA thresholds. This is not an incident, but an early warning for proactive action.
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-xs text-gray-400 uppercase tracking-wider font-medium">
                  Affected Regions
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-800/50 rounded border border-slate-700/50">
                    <div className="text-sm font-medium text-white mb-1">China Mainland</div>
                    <div className="text-xs text-gray-400">{Math.floor(atRiskJobs * 0.35)} jobs</div>
                  </div>
                  <div className="p-3 bg-slate-800/50 rounded border border-slate-700/50">
                    <div className="text-sm font-medium text-white mb-1">Europe</div>
                    <div className="text-xs text-gray-400">{Math.floor(atRiskJobs * 0.28)} jobs</div>
                  </div>
                  <div className="p-3 bg-slate-800/50 rounded border border-slate-700/50">
                    <div className="text-sm font-medium text-white mb-1">United States</div>
                    <div className="text-xs text-gray-400">{Math.floor(atRiskJobs * 0.22)} jobs</div>
                  </div>
                  <div className="p-3 bg-slate-800/50 rounded border border-slate-700/50">
                    <div className="text-sm font-medium text-white mb-1">Singapore</div>
                    <div className="text-xs text-gray-400">{Math.floor(atRiskJobs * 0.15)} jobs</div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs text-gray-400 uppercase tracking-wider font-medium">
                  Estimated Time to Breach
                </div>
                <div className="p-4 bg-slate-800/50 rounded border border-slate-700/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-300">Average time remaining</span>
                    <span className="text-lg font-mono font-bold text-amber-400">2.4 hours</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Based on current backup velocity and SLA windows
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs text-gray-400 uppercase tracking-wider font-medium">
                  Risk Breakdown
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs p-2 bg-slate-800/30 rounded">
                    <span className="text-gray-400">Network congestion</span>
                    <span className="text-white font-medium">{Math.floor(atRiskJobs * 0.45)} jobs</span>
                  </div>
                  <div className="flex items-center justify-between text-xs p-2 bg-slate-800/30 rounded">
                    <span className="text-gray-400">Large dataset size</span>
                    <span className="text-white font-medium">{Math.floor(atRiskJobs * 0.30)} jobs</span>
                  </div>
                  <div className="flex items-center justify-between text-xs p-2 bg-slate-800/30 rounded">
                    <span className="text-gray-400">Resource contention</span>
                    <span className="text-white font-medium">{Math.floor(atRiskJobs * 0.25)} jobs</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-700 flex justify-end">
              <button
                onClick={() => setShowSLAModal(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded transition-colors cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
